/**
 * A ticket's form answers being saved from the list (FormAnswersDialog). One save covers every question of the
 * ticket's forms, so the cells it changed share one state - saving (dim, pulse, locked) and then saved or failed (the
 * green or red box) - as the Team / Group / Assignee cells share a panel's save. Kept outside the cells, which the grid
 * keys by position and replaces on every reload.
 */
export type FormSavePhase = 'saving' | 'saved' | 'failed';

export interface FormSave {
  phase: FormSavePhase;
  /** The question column keys whose answer the save changed, plus the one clicked. */
  keys: Set<number>;
  /** Bumped on every change, so a cell can tell a new outcome from one it has shown. */
  version: number;
  /**
   * The cell that keeps the box once the green has gone (useSaveMark's last edited cell): the one the dialog was
   * opened from if its answer changed, otherwise the first changed one - all of them go green.
   */
  target?: number;
}

const saves = new Map<number, FormSave>();
/** By ticket: only that ticket's cells redraw when its save or open dialog changes, not every form cell on the page. */
const listeners = new Map<number, Set<() => void>>();
let version = 0;

const changed = (ticketId: number) => listeners.get(ticketId)?.forEach(listener => listener());

export const formSaveOf = (ticketId: number) => saves.get(ticketId);

export const startFormSave = (ticketId: number, keys: Iterable<number>, openedFrom?: number) => {
  const changedKeys = new Set(keys);
  const target = openedFrom != null && changedKeys.has(openedFrom) ? openedFrom : changedKeys.values().next().value;
  saves.set(ticketId, { phase: 'saving', keys: changedKeys, version: ++version, target });
  changed(ticketId);
};

export const finishFormSave = (ticketId: number, phase: Exclude<FormSavePhase, 'saving'>) => {
  const save = saves.get(ticketId);
  if (!save) return;
  saves.set(ticketId, { ...save, phase, version: ++version });
  changed(ticketId);
};

export const subscribeFormSaves = (ticketId: number, listener: () => void) => {
  const forTicket = listeners.get(ticketId) ?? new Set<() => void>();
  forTicket.add(listener);
  listeners.set(ticketId, forTicket);
  return () => {
    forTicket.delete(listener);
    if (!forTicket.size) listeners.delete(ticketId);
  };
};

/** The cell whose ticket has its forms dialog open, for the row's editing tint. */
let open: { ticketId: number; columnKey: number } | null = null;

export const formsOpenAt = () => open;

export const setFormsOpen = (at: { ticketId: number; columnKey: number } | null) => {
  const before = open?.ticketId;
  open = at;
  if (before != null) changed(before);
  if (at && at.ticketId !== before) changed(at.ticketId);
};
